import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useLocation } from 'react-router';
import { LoadingState } from '../../../../../common/global/models';
import Carousel from '../../../../../common/widgets/Carousel';
import Loader from '../../../../../common/widgets/Loader';
import { config } from '../../../config';
import { parseProducts } from '../../../helpers';
import { ProductCardModel, ProductListModel } from '../../../models/Product';
import { resources } from '../../../resources/resources';
import { productsService } from '../../../services/products';
import ProductListItem from '../../Common/ProductListItem';

type MostPopularProductsState = {
  productList: ProductListModel[];
  productListState: LoadingState;
};
interface MostPopularProductsProps {
  categoryId?: number;
  showBrands?: boolean;
}

export default function MostPopularProducts(props: MostPopularProductsProps) {
  const location = useLocation();
  const { categoryId, showBrands } = props;
  const { t: translate } = useTranslation();
  const [state, setState] = useState<MostPopularProductsState | null>(null);

  useEffect(() => {
    const abortController = new AbortController();
    setState(null);
    (async () => {
      const result = await productsService.getProductsByMostPopularBrands(
        categoryId,
        config.carouselProductsLength,
        {
          signal: abortController?.signal,
        },
      );
      if (!result.hasErrors) {
        setState({
          productList: result.data,
          productListState: LoadingState.Ready,
        });
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [categoryId]);

  const products = parseProducts(state?.productList, showBrands);

  return (
    <section className="page-section carousel-section mb-60">
      <h2 className="section-title">
        <span>{translate(resources.products.mostPopular)}</span>
      </h2>
      <div className="edn-carousel edn-carousel-dots no-dots-mobile carousel-gap-30 carousel-gap-md-20 carousel-gap-xxs-10">
        {location.pathname === '/' ? (
          <Carousel
            dots={true}
            infinite={false}
            speed={500}
            slidesToShow={4}
            slidesToScroll={(products?.length ?? 0) <= 4 ? 1 : 4}
            arrows={false}
            loading={!state?.productList}
            responsive={[
              {
                breakpoint: 768,
                settings: {
                  slidesToShow: 3,
                  slidesToScroll: (products?.length ?? 0) <= 3 ? 1 : 3,
                },
              },
              {
                breakpoint: 480,
                settings: {
                  slidesToShow: 2.5,
                  slidesToScroll: (products?.length ?? 0) <= 2.5 ? 1 : 2.5,
                },
              },
              {
                breakpoint: 360,
                settings: {
                  slidesToShow: 1.5,
                  slidesToScroll: (products?.length ?? 0) <= 1.5 ? 1 : 1.5,
                },
              },
            ]}
          >
            {_.map(products, (product: ProductCardModel) => {
              return (
                <ProductListItem
                  key={product.productId ?? product.brandId}
                  product={product}
                />
              );
            })}
          </Carousel>
        ) : (
          <Loader loading={!state} loader={<Skeleton />}>
            <div className="card-list card-list-4 card-list-sm-3 card-list-xs-2">
              {!!state &&
                _.map(products?.slice(0, 4), (product: ProductCardModel) => {
                  return (
                    <ProductListItem
                      key={product.productId ?? product.brandId}
                      product={product}
                    />
                  );
                })}
            </div>
          </Loader>
        )}
      </div>
    </section>
  );
}
